'use client';

/**
 * StageEditor Component
 *
 * Modal/panel for editing individual pipeline stage properties
 * including name, color, SLA rules, and other configurations.
 */

import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles, neonAccents } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import type { PipelineStage, StageSLA } from '../../types/pipeline';
import { AnimatedModal } from '../AnimatedModal';
import { GlassButton } from '../GlassButton';
import { GlassSwitch } from '../GlassSwitch';
import { MagicInput } from '../MagicInput';
import { MagicSelect } from '../MagicSelect';

// ============================================================================
// Types
// ============================================================================

export interface StageEditorProps {
  /** Stage being edited (null = new stage) */
  stage: PipelineStage | null;
  /** Whether the editor is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Save handler */
  onSave: (stage: PipelineStage) => void;
  /** Delete handler */
  onDelete?: (stageId: string) => void;
  /** Whether this is the first stage (affects some options) */
  isFirstStage?: boolean;
  /** Whether this is the last stage (affects some options) */
  isLastStage?: boolean;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const colorOptions: Array<{ value: NeonAccent; label: string }> = [
  { value: 'cyan', label: 'Cyan' },
  { value: 'purple', label: 'Purple' },
  { value: 'green', label: 'Green' },
  { value: 'amber', label: 'Amber' },
  { value: 'pink', label: 'Pink' },
  { value: 'red', label: 'Red' },
];

const breachActionOptions = [
  { value: 'notify', label: 'Send Notification' },
  { value: 'escalate', label: 'Escalate' },
  { value: 'auto_move', label: 'Auto Move to Stage' },
];

// ============================================================================
// Styled Components
// ============================================================================

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1.5),
}));

const FormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),

  '& > *': {
    flex: 1,
  },
}));

interface ColorPreviewProps {
  $color: NeonAccent;
  $isSelected: boolean;
  $isDark: boolean;
}

const ColorPreview = styled(motion.button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ColorPreviewProps>(({ $color, $isSelected, $isDark }) => {
  const neonStyles = getNeonStyles($color, $isDark);
  const colors = neonAccents[$color];

  return {
    width: 36,
    height: 36,
    borderRadius: 8,
    border: `2px solid ${$isSelected ? colors.base : 'transparent'}`,
    background: neonStyles.background,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: transitionPresets.allFast,
    padding: 0,

    '&:hover': {
      border: `2px solid ${colors.border}`,
      boxShadow: `0 0 12px ${colors.glow}`,
    },

    '&::after': {
      content: '""',
      width: 12,
      height: 12,
      borderRadius: '50%',
      background: colors.base,
      boxShadow: `0 0 8px ${colors.glow}`,
    },
  };
});

const ColorGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

const SLASection = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ theme, $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    padding: theme.spacing(2),
    borderRadius: 12,
    background: glass.background,
    border: `1px solid ${glass.border}`,
  };
});

const Actions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

// ============================================================================
// Helper Functions
// ============================================================================

const generateId = (): string => {
  return `stage_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const createDefaultStage = (order: number): PipelineStage => ({
  id: generateId(),
  name: '',
  description: '',
  order,
  color: 'cyan',
  allowedTransitions: [],
  isFinal: false,
});

// ============================================================================
// Component
// ============================================================================

export function StageEditor({
  stage,
  open,
  onClose,
  onSave,
  onDelete,
  isFirstStage: _isFirstStage = false,
  isLastStage: _isLastStage = false,
  neonAccent: _neonAccent = 'cyan',
  className,
}: StageEditorProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Form state
  const [formData, setFormData] = useState<PipelineStage>(stage || createDefaultStage(0));
  const [showSLA, setShowSLA] = useState(!!stage?.sla);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when stage changes
  useEffect(() => {
    if (stage) {
      setFormData(stage);
      setShowSLA(!!stage.sla);
    } else {
      setFormData(createDefaultStage(0));
      setShowSLA(false);
    }
    setErrors({});
  }, [stage, open]);

  // Handle field change
  const handleChange = useCallback((field: keyof PipelineStage, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // Handle SLA change
  const handleSLAChange = useCallback((field: keyof StageSLA, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      sla: {
        targetHours: prev.sla?.targetHours ?? 24,
        warningHours: prev.sla?.warningHours ?? 4,
        isEnforced: prev.sla?.isEnforced ?? true,
        ...prev.sla,
        [field]: value,
      },
    }));
  }, []);

  // Toggle SLA
  const handleToggleSLA = useCallback((enabled: boolean) => {
    setShowSLA(enabled);
    if (!enabled) {
      setFormData((prev) => ({
        ...prev,
        sla: undefined,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        sla: {
          targetHours: 24,
          warningHours: 4,
          isEnforced: true,
        },
      }));
    }
  }, []);

  // Validate form
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors['name'] = 'Stage name is required';
    }

    if (showSLA && formData.sla) {
      if (formData.sla.targetHours <= 0) {
        newErrors['targetHours'] = 'Target hours must be positive';
      }
      if (formData.sla.warningHours >= formData.sla.targetHours) {
        newErrors['warningHours'] = 'Warning must be before target';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, showSLA]);

  // Handle save
  const handleSave = useCallback(() => {
    if (validate()) {
      onSave(formData);
      onClose();
    }
  }, [formData, validate, onSave, onClose]);

  // Handle delete
  const handleDelete = useCallback(() => {
    if (stage && onDelete) {
      onDelete(stage.id);
      onClose();
    }
  }, [stage, onDelete, onClose]);

  const isNewStage = !stage;

  return (
    <AnimatedModal
      open={open}
      onClose={onClose}
      title={isNewStage ? 'Add Stage' : 'Edit Stage'}
      subtitle={isNewStage ? 'Configure the new pipeline stage' : `Editing: ${stage?.name}`}
      maxWidth="sm"
      className={className}
      actions={
        <Actions>
          <Box>
            {!isNewStage && onDelete && (
              <GlassButton variant="outlined" color="error" size="small" onClick={handleDelete}>
                Delete Stage
              </GlassButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <GlassButton variant="outlined" color="secondary" onClick={onClose}>
              Cancel
            </GlassButton>
            <GlassButton variant="gradient" onClick={handleSave}>
              {isNewStage ? 'Add Stage' : 'Save Changes'}
            </GlassButton>
          </Box>
        </Actions>
      }
    >
      <motion.div {...motionPresets.fadeIn}>
        {/* Basic Info */}
        <FormSection>
          <SectionTitle>Basic Information</SectionTitle>
          <FormRow>
            <MagicInput
              label="Stage Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Qualification, Negotiation"
              error={!!errors['name']}
              helperText={errors['name']}
              fullWidth
            />
          </FormRow>
          <FormRow>
            <MagicInput
              label="Description"
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description"
              multiline
              rows={2}
              fullWidth
            />
          </FormRow>
        </FormSection>

        {/* Color Selection */}
        <FormSection>
          <SectionTitle>Stage Color</SectionTitle>
          <ColorGrid>
            {colorOptions.map((option) => (
              <ColorPreview
                key={option.value}
                $color={option.value}
                $isSelected={formData.color === option.value}
                $isDark={isDark}
                onClick={() => handleChange('color', option.value)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={option.label}
              />
            ))}
          </ColorGrid>
        </FormSection>

        {/* Stage Options */}
        <FormSection>
          <SectionTitle>Stage Options</SectionTitle>
          <FormRow>
            <MagicInput
              label="WIP Limit"
              type="number"
              value={formData.wipLimit?.toString() || ''}
              onChange={(e) => handleChange('wipLimit', e.target.value ? parseInt(e.target.value, 10) : undefined)}
              placeholder="No limit"
              helperText="Maximum records in this stage"
              fullWidth
            />
          </FormRow>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <GlassSwitch checked={formData.isFinal} onChange={(_e, checked) => handleChange('isFinal', checked)} />
              }
              label="Final Stage (end of pipeline)"
            />
            {formData.isFinal && (
              <Box sx={{ pl: 4, display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <GlassSwitch
                      checked={formData.isSuccess || false}
                      onChange={(_e, checked) => {
                        handleChange('isSuccess', checked);
                        if (checked) {
                          handleChange('isFailure', false);
                        }
                      }}
                    />
                  }
                  label="Success outcome"
                />
                <FormControlLabel
                  control={
                    <GlassSwitch
                      checked={formData.isFailure || false}
                      onChange={(_e, checked) => {
                        handleChange('isFailure', checked);
                        if (checked) {
                          handleChange('isSuccess', false);
                        }
                      }}
                    />
                  }
                  label="Failure outcome"
                />
              </Box>
            )}
          </Box>
        </FormSection>

        {/* SLA Configuration */}
        <FormSection>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
            }}
          >
            <SectionTitle sx={{ mb: 0 }}>SLA Configuration</SectionTitle>
            <GlassSwitch size="small" checked={showSLA} onChange={(_e, checked) => handleToggleSLA(checked)} />
          </Box>

          {showSLA && (
            <SLASection $isDark={isDark}>
              <FormRow>
                <MagicInput
                  label="Target Hours"
                  type="number"
                  value={formData.sla?.targetHours?.toString() || '24'}
                  onChange={(e) => handleSLAChange('targetHours', parseInt(e.target.value, 10) || 24)}
                  error={!!errors['targetHours']}
                  helperText={errors['targetHours'] || 'Time to complete stage'}
                  fullWidth
                />
                <MagicInput
                  label="Warning Hours"
                  type="number"
                  value={formData.sla?.warningHours?.toString() || '4'}
                  onChange={(e) => handleSLAChange('warningHours', parseInt(e.target.value, 10) || 4)}
                  error={!!errors['warningHours']}
                  helperText={errors['warningHours'] || 'Hours before target to warn'}
                  fullWidth
                />
              </FormRow>
              <FormRow>
                <MagicSelect
                  label="On Breach Action"
                  value={formData.sla?.breachAction || 'notify'}
                  onChange={(e) => handleSLAChange('breachAction', e.target.value as StageSLA['breachAction'])}
                  options={breachActionOptions}
                  fullWidth
                />
              </FormRow>
              <FormControlLabel
                control={
                  <GlassSwitch
                    checked={formData.sla?.isEnforced ?? true}
                    onChange={(_e, checked) => handleSLAChange('isEnforced', checked)}
                  />
                }
                label="Enforce SLA (track compliance)"
              />
            </SLASection>
          )}
        </FormSection>
      </motion.div>
    </AnimatedModal>
  );
}

StageEditor.displayName = 'StageEditor';

export default StageEditor;
